import { IconChevronRight } from '@hulu-react-style-components/icons';
import React from 'react';
import type { Row } from 'react-table';
import { CSSTransition } from 'react-transition-group';
import type { ListRowProps } from 'react-virtualized';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';

import type { TargetingTermValue } from '../../../models';
import { workWithLocalStorage } from '../../../utils/storage/localStorage';
import { DIMENSION_INCLUDE_EXCLUDE_LIST } from '../../TargetingDropdowns/constants';
import { LOCAL_STORAGE_EXPANDABLE_ROWS_STATE } from '../constants';

export interface GroupedRowData {
  key: string;
  items: Row<TargetingTermValue>[];
  category: string;
  dimension: string;
  include: boolean;
}

type ElementFunction = (baseClass: string, modifier?: string) => string;

export const buildRowGroups = (rows: Row<TargetingTermValue>[]): { [key: string]: GroupedRowData } => {
  const groupedRowData = rows.reduce((acc, row) => {
    const dimensionKey = row.original.dimension.id;
    const key = DIMENSION_INCLUDE_EXCLUDE_LIST.includes(dimensionKey)
      ? `${row.original.category.displayName}-${row.original.dimension.displayName}-${row.original.include}`
      : `${row.original.category.displayName}-${row.original.dimension.displayName}`;

    if (!acc[key]) {
      acc[key] = {
        key,
        items: [],
        category: row.original.category.displayName,
        dimension: row.original.dimension.displayName,
        include: row.original.include,
      };
    }

    acc[key].items.push(row);

    return acc;
  }, {} as { [key: string]: GroupedRowData });

  return groupedRowData;
};

export const generateRow = (
  termValueRow: Row<TargetingTermValue>,
  nThRowForGeneratingKey: number,
  rowIndexWithinGroup: number,
  groupType: string | undefined,
  element: ElementFunction,
  groupLength?: number,
  groupExpanded?: boolean,
  toggle?: () => void
): React.JSX.Element => {
  return (
    <table className={element('sub-row-table')} key={`sub-table-${nThRowForGeneratingKey}`}>
      <tbody className={element('sub-row-body')} key={`sub-body-${nThRowForGeneratingKey}`}>
        <tr {...termValueRow.getRowProps()} key={`sub-row-${nThRowForGeneratingKey}`}>
          {termValueRow.cells.map((cell, colIndex) => {
            return (
              <td {...cell.getCellProps()} tabIndex={-1}>
                {cell.render('Cell', {
                  isLineItemTargetingTermValue: groupType === 'Line Item Targeting',
                })}
                {groupLength !== undefined && !groupExpanded && rowIndexWithinGroup === 0 && colIndex === 2 && (
                  <button className="expand-row-link" onClick={toggle}>
                    (+{groupLength} more)
                  </button>
                )}
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
};

const cellMeasurerCacheDefaultProps = {
  fixedWidth: true,
  defaultHeight: 50,
};

export const generateToggleRow = (
  rowGroup: React.JSX.Element[],
  groupExpanded: boolean,
  element: ElementFunction,
  toggle: () => void,
  groupKey: string
): React.JSX.Element => {
  const cache = new CellMeasurerCache(cellMeasurerCacheDefaultProps);

  // first child is the group header row so we start displaying from the next index
  const rowRenderer = ({ index, key, parent, style }: ListRowProps): JSX.Element => (
    <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
      <div style={style}>{rowGroup[index + 1]}</div>
    </CellMeasurer>
  );

  return (
    <div className={element('toggle-row')} key={`toggle-row-${groupKey}`}>
      <div className={element('toggle-top-row', groupExpanded ? 'active' : '')}>
        <div className={element('toggle-top-row-wrapper')}>
          <IconChevronRight className="chevron" onClick={toggle} />
          <div className={element('toggle-row-first-child')}>{rowGroup[0]}</div>
        </div>
      </div>
      <CSSTransition classNames={element('toggle')} in={groupExpanded} timeout={150} unmountOnExit appear>
        <div className={element('toggle-row-children')}>
          <AutoSizer disableHeight>
            {({ width }): JSX.Element => (
              <List
                className={element('toggle-row-scrollable-list')}
                deferredMeasurementCache={cache}
                height={300}
                overscanRowCount={25}
                rowCount={rowGroup.length - 1}
                rowHeight={cache.rowHeight}
                rowRenderer={rowRenderer}
                width={width}
              />
            )}
          </AutoSizer>
        </div>
      </CSSTransition>
    </div>
  );
};

export const getExpandCollapseText = (expandedGroups: Record<string, boolean>): string => {
  let allExpanded;
  if (Object.keys(expandedGroups).length) {
    allExpanded = Object.values(expandedGroups).every((isExpanded) => isExpanded === true);
    workWithLocalStorage.setData(LOCAL_STORAGE_EXPANDABLE_ROWS_STATE, allExpanded);
  } else {
    allExpanded = workWithLocalStorage.getData(LOCAL_STORAGE_EXPANDABLE_ROWS_STATE);
  }

  return allExpanded ? 'Collapse All' : 'Expand All';
};
